import React, { useState, useEffect, useCallback } from 'react';
import {
  FaCog, FaBuilding, FaMapMarkerAlt, FaClock, FaCalendarAlt, FaPlaneDeparture,
  FaPlus, FaEdit, FaTrash, FaSave,
} from 'react-icons/fa';
import api, { errorMessage } from '../api';
import {
  PageShell, Card, Badge, Button, Modal, Field, inputClass, Alert, Loading, Table,
} from './attendance/ui';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Editable resource table shared by departments, locations, shifts, etc. */
const ResourceSection = ({ title, icon, endpoint, columns, blank, fields,
  renderRow, onChanged, items, setError }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    const payload = Object.fromEntries(
      Object.entries(form).filter(([, value]) => value !== '' && value !== null));
    try {
      if (editing === 'new') await api.post(endpoint, payload);
      else await api.put(`${endpoint}${editing.id}/`, payload);
      setEditing(null);
      await onChanged();
    } catch (err) {
      setError(errorMessage(err, `Could not save this ${title.toLowerCase()} entry.`));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await api.delete(`${endpoint}${item.id}/`);
      await onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not delete the entry. It may still be in use.'));
    }
  };

  return (
    <Card
      title={<span className="flex items-center gap-2">{icon} {title} ({items.length})</span>}
      action={<Button variant="secondary" onClick={() => { setForm(blank); setEditing('new'); }}>
        <FaPlus /> Add
      </Button>}
      className="mb-6"
    >
      <Table
        columns={[...columns, 'Actions']}
        rows={items}
        empty={`No ${title.toLowerCase()} configured yet.`}
        renderRow={(item) => (
          <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
            {renderRow(item)}
            <td className="py-3 pr-4">
              <div className="flex gap-1">
                <button onClick={() => { setForm({ ...blank, ...item }); setEditing(item); }}
                  className="p-2 text-gray-400 hover:text-[#15A857] hover:bg-green-50 rounded-lg" title="Edit">
                  <FaEdit />
                </button>
                <button onClick={() => remove(item)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title="Delete">
                  <FaTrash />
                </button>
              </div>
            </td>
          </tr>
        )}
      />

      <Modal isOpen={!!editing} onClose={() => setEditing(null)}
        title={editing === 'new' ? `Add ${title.toLowerCase()}` : `Edit ${editing?.name || ''}`}
        size="max-w-2xl">
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((field) => (
              <Field key={field.key} label={field.label} hint={field.hint}>
                {field.type === 'select' ? (
                  <select value={form[field.key] ?? ''} className={inputClass} required={field.required}
                    onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}>
                    {field.options.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                ) : field.type === 'checkbox' ? (
                  <input type="checkbox" checked={Boolean(form[field.key])}
                    className="w-5 h-5 rounded accent-[#15A857]"
                    onChange={(event) => setForm({ ...form, [field.key]: event.target.checked })} />
                ) : (
                  <input type={field.type || 'text'} value={form[field.key] ?? ''}
                    required={field.required} className={inputClass} placeholder={field.placeholder}
                    onChange={(event) => setForm({ ...form, [field.key]: event.target.value })} />
                )}
              </Field>
            ))}
          </div>
          <div className="flex gap-3">
            <Button type="submit" disabled={saving} className="flex-1">
              <FaSave /> {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
};

/** System administration: institution settings and every reference table. */
const SystemSettings = () => {
  const [tab, setTab] = useState('general');
  const [settings, setSettings] = useState(null);
  const [data, setData] = useState({
    departments: [], locations: [], shifts: [], holidays: [], leaveTypes: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [settingsRes, departments, locations, shifts, holidays, leaveTypes] =
        await Promise.all([
          api.get('settings/'), api.get('departments/'), api.get('locations/'),
          api.get('shifts/'), api.get('holidays/'), api.get('leave-types/'),
        ]);
      setSettings(settingsRes.data);
      setData({
        departments: departments.data, locations: locations.data,
        shifts: shifts.data, holidays: holidays.data, leaveTypes: leaveTypes.data,
      });
    } catch (err) {
      setError(errorMessage(err, 'Could not load system settings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.patch('settings/', settings);
      setNotice('Settings saved.');
    } catch (err) {
      setError(errorMessage(err, 'Could not save the settings.'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => (event) => setSettings({ ...settings, [key]: event.target.checked });

  if (loading) return <PageShell title="System Settings"><Loading /></PageShell>;

  const tabs = [
    ['general', 'General', <FaCog key="c" />],
    ['departments', 'Departments', <FaBuilding key="b" />],
    ['locations', 'Locations', <FaMapMarkerAlt key="m" />],
    ['shifts', 'Shifts', <FaClock key="s" />],
    ['holidays', 'Holidays', <FaCalendarAlt key="h" />],
    ['leave', 'Leave types', <FaPlaneDeparture key="l" />],
  ];

  return (
    <PageShell title="System Settings" subtitle="Institution configuration and reference data">
      <Alert>{error}</Alert>
      <Alert tone="success">{notice}</Alert>

      <div className="flex gap-2 mb-6 flex-wrap">
        {tabs.map(([key, label, icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${tab === key ? 'bg-[#15A857] text-white shadow-lg shadow-[#15A857]/20' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            {icon} {label}
          </button>
        ))}
      </div>

      {tab === 'general' && settings && (
        <Card title="Institution settings">
          <form onSubmit={saveSettings} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Institution name">
                <input value={settings.institutionName || ''} className={inputClass}
                  onChange={(event) => setSettings({ ...settings, institutionName: event.target.value })} />
              </Field>
              <Field label="Default shift" hint="Applied to staff without their own shift.">
                <select value={settings.defaultShift || ''} className={inputClass}
                  onChange={(event) => setSettings({ ...settings, defaultShift: event.target.value })}>
                  <option value="">None</option>
                  {data.shifts.map((shift) => <option key={shift.id} value={shift.name}>{shift.name}</option>)}
                </select>
              </Field>
              <Field label="Monthly report due day"
                hint="Day of the following month a rural report is expected.">
                <input type="number" min="1" max="28" value={settings.monthlySubmissionDueDay}
                  className={inputClass}
                  onChange={(event) => setSettings({ ...settings, monthlySubmissionDueDay: event.target.value })} />
              </Field>
            </div>

            <div>
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Attendance</h3>
              <div className="space-y-3">
                {[
                  ['allowSelfCheckIn', 'Allow staff to check in and out from the portal'],
                  ['autoAbsentMarking', 'Mark staff with no punch as absent when a day is closed'],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={Boolean(settings[key])} onChange={toggle(key)}
                      className="w-5 h-5 rounded accent-[#15A857]" />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Notifications</h3>
              <div className="space-y-3">
                {[
                  ['notifyOnLeaveRequest', 'Notify approvers when leave is requested'],
                  ['notifyOnLeaveDecision', 'Notify staff when their leave is decided'],
                  ['notifyOnLateArrival', 'Notify staff when they are recorded as late'],
                  ['notifyOnMissingAttendance', 'Notify staff when a day has no attendance record'],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={Boolean(settings[key])} onChange={toggle(key)}
                      className="w-5 h-5 rounded accent-[#15A857]" />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <Button type="submit" disabled={saving}>
              <FaSave /> {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </form>
        </Card>
      )}

      {tab === 'departments' && (
        <ResourceSection
          title="Departments" icon={<FaBuilding className="text-gray-400" />}
          endpoint="departments/" items={data.departments} onChanged={load} setError={setError}
          columns={['Name', 'Code', 'Head', 'Staff', 'Status']}
          blank={{ name: '', code: '', description: '', head: '' }}
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'code', label: 'Code', required: true, placeholder: 'SCI' },
            { key: 'head', label: 'Department head', hint: 'Staff ID or name.' },
            { key: 'description', label: 'Description' },
          ]}
          renderRow={(item) => (
            <>
              <td className="py-3 pr-4 font-medium text-gray-800">{item.name}</td>
              <td className="py-3 pr-4 font-mono text-xs">{item.code}</td>
              <td className="py-3 pr-4">{item.headName || '—'}</td>
              <td className="py-3 pr-4">{item.staffCount}</td>
              <td className="py-3 pr-4"><Badge value={item.isActive ? 'active' : 'inactive'} /></td>
            </>
          )}
        />
      )}

      {tab === 'locations' && (
        <ResourceSection
          title="Locations" icon={<FaMapMarkerAlt className="text-gray-400" />}
          endpoint="locations/" items={data.locations} onChanged={load} setError={setError}
          columns={['Name', 'Code', 'Type', 'City', 'Region', 'Staff']}
          blank={{ name: '', code: '', type: 'urban', address: '', city: '', region: '' }}
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'code', label: 'Code', required: true, placeholder: 'MAIN' },
            {
              key: 'type', label: 'Type', type: 'select',
              hint: 'Urban sites use biometric devices; rural sites use supervisor reporting.',
              options: [['urban', 'Urban (biometric)'], ['rural', 'Rural (supervisor reporting)']],
            },
            { key: 'address', label: 'Address' },
            { key: 'city', label: 'City' },
            { key: 'region', label: 'Region' },
          ]}
          renderRow={(item) => (
            <>
              <td className="py-3 pr-4 font-medium text-gray-800">{item.name}</td>
              <td className="py-3 pr-4 font-mono text-xs">{item.code}</td>
              <td className="py-3 pr-4 capitalize">{item.type}</td>
              <td className="py-3 pr-4">{item.city || '—'}</td>
              <td className="py-3 pr-4">{item.region || '—'}</td>
              <td className="py-3 pr-4">{item.staffCount}</td>
            </>
          )}
        />
      )}

      {tab === 'shifts' && (
        <ResourceSection
          title="Shifts" icon={<FaClock className="text-gray-400" />}
          endpoint="shifts/" items={data.shifts} onChanged={load} setError={setError}
          columns={['Name', 'Hours', 'Grace', 'Full day', 'Overtime after', 'Working days', 'Default']}
          blank={{
            name: '', startTime: '08:30', endTime: '17:00', graceMinutes: 15,
            earlyDepartureGraceMinutes: 15, breakMinutes: 60, fullDayMinutes: 450,
            halfDayMinutes: 225, overtimeAfterMinutes: 450, workingDays: '0,1,2,3,4',
          }}
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'startTime', label: 'Start time', type: 'time', required: true },
            { key: 'endTime', label: 'End time', type: 'time', required: true },
            { key: 'graceMinutes', label: 'Late grace (min)', type: 'number' },
            { key: 'earlyDepartureGraceMinutes', label: 'Early-leave grace (min)', type: 'number' },
            { key: 'breakMinutes', label: 'Unpaid break (min)', type: 'number' },
            { key: 'fullDayMinutes', label: 'Full day (min)', type: 'number' },
            { key: 'halfDayMinutes', label: 'Half day (min)', type: 'number' },
            { key: 'overtimeAfterMinutes', label: 'Overtime after (min)', type: 'number' },
            {
              key: 'workingDays', label: 'Working days',
              hint: 'Comma separated, Monday = 0 … Sunday = 6.', placeholder: '0,1,2,3,4',
            },
          ]}
          renderRow={(item) => (
            <>
              <td className="py-3 pr-4 font-medium text-gray-800">{item.name}</td>
              <td className="py-3 pr-4 whitespace-nowrap">
                {String(item.startTime).slice(0, 5)}–{String(item.endTime).slice(0, 5)}
              </td>
              <td className="py-3 pr-4">{item.graceMinutes} min</td>
              <td className="py-3 pr-4">{Math.round(item.fullDayMinutes / 60 * 10) / 10} h</td>
              <td className="py-3 pr-4">{Math.round(item.overtimeAfterMinutes / 60 * 10) / 10} h</td>
              <td className="py-3 pr-4 text-xs">
                {String(item.workingDays).split(',')
                  .map((day) => WEEKDAYS[Number(day)] || day).join(', ')}
              </td>
              <td className="py-3 pr-4">{item.isDefault ? <Badge value="approved" label="Default" /> : '—'}</td>
            </>
          )}
        />
      )}

      {tab === 'holidays' && (
        <ResourceSection
          title="Holidays" icon={<FaCalendarAlt className="text-gray-400" />}
          endpoint="holidays/" items={data.holidays} onChanged={load} setError={setError}
          columns={['Name', 'Date', 'Recurring', 'Location']}
          blank={{ name: '', date: '', isRecurring: false, location: '', description: '' }}
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'date', label: 'Date', type: 'date', required: true },
            { key: 'isRecurring', label: 'Repeats every year', type: 'checkbox' },
            {
              key: 'location', label: 'Location', type: 'select',
              hint: 'Leave empty to apply institution-wide.',
              options: [['', 'All locations'], ...data.locations.map((item) => [item.name, item.name])],
            },
            { key: 'description', label: 'Description' },
          ]}
          renderRow={(item) => (
            <>
              <td className="py-3 pr-4 font-medium text-gray-800">{item.name}</td>
              <td className="py-3 pr-4 whitespace-nowrap">{item.date}</td>
              <td className="py-3 pr-4">{item.isRecurring ? 'Yes' : 'No'}</td>
              <td className="py-3 pr-4">{item.location || 'All locations'}</td>
            </>
          )}
        />
      )}

      {tab === 'leave' && (
        <ResourceSection
          title="Leave types" icon={<FaPlaneDeparture className="text-gray-400" />}
          endpoint="leave-types/" items={data.leaveTypes} onChanged={load} setError={setError}
          columns={['Name', 'Code', 'Days / year', 'Paid', 'Needs approval', 'Status']}
          blank={{
            name: '', code: '', daysAllowedPerYear: 0, isPaid: true,
            requiresApproval: true, requiresAttachment: false, color: '#15A857',
          }}
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'code', label: 'Code', required: true, placeholder: 'ANNUAL' },
            { key: 'daysAllowedPerYear', label: 'Days per year', type: 'number' },
            { key: 'color', label: 'Colour', type: 'color' },
            { key: 'isPaid', label: 'Paid leave', type: 'checkbox' },
            { key: 'requiresApproval', label: 'Requires approval', type: 'checkbox' },
            { key: 'requiresAttachment', label: 'Requires attachment', type: 'checkbox' },
          ]}
          renderRow={(item) => (
            <>
              <td className="py-3 pr-4 font-medium text-gray-800">
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  {item.name}
                </span>
              </td>
              <td className="py-3 pr-4 font-mono text-xs">{item.code}</td>
              <td className="py-3 pr-4">{item.daysAllowedPerYear}</td>
              <td className="py-3 pr-4">{item.isPaid ? 'Yes' : 'No'}</td>
              <td className="py-3 pr-4">{item.requiresApproval ? 'Yes' : 'No'}</td>
              <td className="py-3 pr-4"><Badge value={item.isActive ? 'active' : 'inactive'} /></td>
            </>
          )}
        />
      )}
    </PageShell>
  );
};

export default SystemSettings;
